import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import {
  useSmoothScroll, smoothScrollTo, lockScroll, useRevealAll,
  useIntro, IntroLoader, Marquee, useStackedSections,
} from './motion';
import './Home.css';

const get = (path, fallback) =>
  axios.get(`${API}${path}`).then(r => r.data).catch(() => fallback);

async function fetchAll() {
  const [portfolio, services, settings, videos, team, brands] = await Promise.all([
    get('/api/portfolio',             []),
    get('/api/content/services',      []),
    get('/api/content/settings',      {}),
    get('/api/videos',                []),
    get('/api/team',                  []),
    get('/api/brands',                []),
  ]);
  return { portfolio, services, settings, videos, team, brands };
}

function getYouTubeId(url = '') {
  const m = url.match(
    /(?:youtube\.com\/(?:watch\?.*v=|embed\/|shorts\/|v\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
  );
  return m ? m[1] : null;
}

const DEFAULT_ABOUT_IMAGE =
  'https://images.unsplash.com/photo-1581591524425-c7e0978865fc?auto=format&fit=crop&w=800&q=80';

const DEFAULT_HERO_IMAGE =
  'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?auto=format&fit=crop&w=1800&q=80';

// Slideshow slides are stored as JSON: [{ type: 'image'|'video'|'youtube', url }]
// (plain-string entries are typed by their link)
const isVideoUrl = (url = '') => /\.(mp4|webm|mov)(\?|#|$)/i.test(url);
const slideTypeOf = (url = '') => getYouTubeId(url) ? 'youtube' : isVideoUrl(url) ? 'video' : 'image';
function parseSlides(raw) {
  try {
    const a = JSON.parse(raw || '[]');
    if (!Array.isArray(a)) return [];
    return a
      .map(s => typeof s === 'string' ? { type: slideTypeOf(s), url: s } : s)
      .filter(s => s && s.url && (s.type !== 'youtube' || getYouTubeId(s.url)));
  } catch { return []; }
}

// Load YouTube's IFrame Player API once (needed to know when a video ends)
let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve, reject) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      s.async = true;
      s.onerror = () => { ytApiPromise = null; reject(new Error('YouTube API failed to load')); };
      document.head.appendChild(s);
    });
  }
  return ytApiPromise;
}

// One YouTube slide: plays from the start when it becomes active, pauses when
// not, reports when it ends, and gives up (onFail) if it can't play.
function YouTubeSlide({ url, active, muted, single, onEnded, onFail }) {
  const wrapRef   = useRef(null);
  const playerRef = useRef(null);
  const readyRef  = useRef(false);
  const live = useRef({});
  live.current = { active, muted, single, onEnded, onFail };
  const videoId = getYouTubeId(url);

  useEffect(() => {
    let cancelled = false;
    // YouTube replaces its target element with an iframe, so give it a node
    // React doesn't manage
    const host = document.createElement('div');
    wrapRef.current.appendChild(host);

    loadYouTubeApi().then(YT => {
      if (cancelled) return;
      playerRef.current = new YT.Player(host, {
        videoId,
        playerVars: {
          autoplay: 0, controls: 0, mute: 1, playsinline: 1, rel: 0,
          modestbranding: 1, disablekb: 1, iv_load_policy: 3, fs: 0,
        },
        events: {
          onReady: (e) => {
            readyRef.current = true;
            e.target.mute();
            if (live.current.active) {
              if (!live.current.muted) e.target.unMute();
              e.target.playVideo();
            }
          },
          onStateChange: (e) => {
            if (e.data !== YT.PlayerState.ENDED || !live.current.active) return;
            if (live.current.single) { e.target.seekTo(0, true); e.target.playVideo(); }
            else live.current.onEnded();
          },
          onError: () => { if (live.current.active) live.current.onFail(); },
        },
      });
    }).catch(() => { if (live.current.active) live.current.onFail(); });

    return () => {
      cancelled = true;
      try { playerRef.current?.destroy(); } catch {}
      playerRef.current = null;
      readyRef.current = false;
      host.remove();
    };
  }, [videoId]);

  // play from the start when shown, pause when hidden
  useEffect(() => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    if (active) { p.seekTo(0, true); p.playVideo(); } else p.pauseVideo();
  }, [active]);

  useEffect(() => {
    const p = playerRef.current;
    if (!p || !readyRef.current) return;
    if (muted) p.mute(); else p.unMute();
  }, [muted]);

  // if it hasn't started within 10s (blocked, unavailable…), move on
  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      const state = playerRef.current?.getPlayerState?.();
      if (![1, 2, 3].includes(state)) live.current.onFail(); // playing / paused / buffering
    }, 10000);
    return () => clearTimeout(t);
  }, [active]);

  return <div ref={wrapRef} className={`hero-slide hero-slide-yt${active ? ' active' : ''}`} />;
}

// Cross-fading slideshow of images and videos. Images stay for `interval`
// seconds (with a slow zoom); videos play to the end, then it moves on.
function HeroSlideshow({ slides, interval, muted }) {
  const [index, setIndex] = useState(0);
  const videoRefs = useRef([]);
  const count = slides.length;
  const next = useCallback(() => setIndex(i => (i + 1) % count), [count]);
  const prev = useCallback(() => setIndex(i => (i - 1 + count) % count), [count]);
  const current = slides[index];

  useEffect(() => {
    // pause every video that isn't on screen
    videoRefs.current.forEach((v, i) => { if (v && i !== index) v.pause(); });
    if (!current) return;
    if (current.type === 'youtube') return; // YouTubeSlide drives its own timing

    if (current.type === 'video') {
      const v = videoRefs.current[index];
      if (!v) return;
      v.currentTime = 0;
      v.muted = muted;
      let fallback = null;
      v.play().catch(() => { fallback = setTimeout(next, interval * 1000); }); // autoplay blocked
      return () => clearTimeout(fallback);
    }
    if (count < 2) return;
    const t = setTimeout(next, interval * 1000);
    return () => clearTimeout(t);
    // `muted` is applied separately below, so it doesn't restart the slide
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, count, interval, next]);

  // sound toggle applies to whichever video is playing
  useEffect(() => {
    const v = videoRefs.current[index];
    if (v) {
      v.muted = muted;
      if (!muted) v.play().catch(() => {});
    }
  }, [muted, index]);

  const onEnded = (i) => {
    if (i !== index) return;
    if (count < 2) { const v = videoRefs.current[i]; v.currentTime = 0; v.play().catch(() => {}); }
    else next();
  };

  return (
    <>
      <div className="hero-bg hero-slides" aria-hidden="true">
        {slides.map((s, i) => {
          const active = i === index;
          const near = active || i === (index + 1) % count;
          if (s.type === 'youtube') {
            return (
              <YouTubeSlide
                key={`${s.url}-${i}`}
                url={s.url}
                active={active}
                muted={muted}
                single={count < 2}
                onEnded={next}
                onFail={count > 1 ? next : () => {}}
              />
            );
          }
          return s.type === 'video' ? (
            <video
              key={`${s.url}-${i}`}
              ref={el => { videoRefs.current[i] = el; }}
              className={`hero-slide${active ? ' active' : ''}`}
              src={s.url}
              muted
              playsInline
              preload={near ? 'auto' : 'metadata'}
              onEnded={() => onEnded(i)}
              onError={() => { if (i === index && count > 1) next(); }}
            />
          ) : (
            <div
              key={`${s.url}-${i}`}
              className={`hero-slide hero-slide-image${active ? ' active' : ''}`}
              style={{ backgroundImage: `url(${s.url})` }}
            />
          );
        })}
      </div>
      {count > 1 && (
        <div className="hero-slide-controls">
          <button type="button" className="hero-slide-arrow" onClick={prev} aria-label="Previous slide">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
          </button>
          <div className="hero-dots" role="tablist" aria-label="Background slides">
            {slides.map((s, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Slide ${i + 1}${s.type !== 'image' ? ' (video)' : ''}`}
                className={i === index ? 'active' : ''}
                onClick={() => setIndex(i)}
              />
            ))}
          </div>
          <button type="button" className="hero-slide-arrow" onClick={next} aria-label="Next slide">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>
      )}
    </>
  );
}

// Hero background from admin settings: image, uploaded/linked video, YouTube,
// or a slideshow mixing images and videos. The image is the poster while a
// video loads, and the fallback if it fails.
// Videos always start muted (browsers only autoplay muted video); the sound
// button lets visitors turn audio on. `forceMute` silences it again, e.g.
// while another video plays in the lightbox.
function HeroBackground({ settings, forceMute = false }) {
  const image = settings.hero_bg_image || DEFAULT_HERO_IMAGE;
  const video = settings.hero_bg_type === 'video' ? (settings.hero_bg_video || '') : '';
  const ytId  = video ? getYouTubeId(video) : null;
  const slides = settings.hero_bg_type === 'slideshow' ? parseSlides(settings.hero_bg_slides) : [];
  const slideInterval = Math.min(30, Math.max(2, Number(settings.hero_bg_slide_interval) || 6));
  const [videoFailed, setVideoFailed] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);
  const ytRef    = useRef(null);

  const applyMute = useCallback((m) => {
    const v = videoRef.current;
    if (v) {
      v.muted = m;
      if (!m) v.play().catch(() => {});
    }
    // YouTube iframe API (enabled via enablejsapi=1) accepts postMessage commands
    ytRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func: m ? 'mute' : 'unMute', args: [] }), '*'
    );
    if (!m) {
      ytRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'setVolume', args: [100] }), '*'
      );
    }
  }, []);

  const toggle = () => { const m = !muted; setMuted(m); applyMute(m); };

  useEffect(() => {
    if (forceMute && !muted) { setMuted(true); applyMute(true); }
  }, [forceMute, muted, applyMute]);

  const soundButton = (
    <button
      type="button"
      className={`hero-sound${muted ? '' : ' on'}`}
      onClick={toggle}
      aria-pressed={!muted}
      aria-label={muted ? 'Turn sound on' : 'Turn sound off'}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
        {muted
          ? <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />
          : <path d="M16.5 8.5a5 5 0 010 7M19 6a8.5 8.5 0 010 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" />}
      </svg>
      <span>{muted ? 'Sound off' : 'Sound on'}</span>
    </button>
  );

  if (slides.length > 0) {
    return (
      <>
        <HeroSlideshow slides={slides} interval={slideInterval} muted={muted} />
        {slides.some(s => s.type !== 'image') && soundButton}
      </>
    );
  }

  if (ytId) {
    const params = `autoplay=1&mute=1&loop=1&playlist=${ytId}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1&disablekb=1&iv_load_policy=3&enablejsapi=1`;
    return (
      <>
        <div className="hero-bg hero-bg-media" style={{ backgroundImage: `url(${image})` }}>
          <iframe
            ref={ytRef}
            className="hero-bg-youtube"
            src={`https://www.youtube.com/embed/${ytId}?${params}`}
            title="Background video"
            allow="autoplay; encrypted-media"
            tabIndex={-1}
            aria-hidden="true"
          />
        </div>
        {soundButton}
      </>
    );
  }

  if (video && !videoFailed) {
    return (
      <>
        <div className="hero-bg hero-bg-media" style={{ backgroundImage: `url(${image})` }}>
          <video
            ref={videoRef}
            className="hero-bg-video"
            src={video}
            poster={image}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden="true"
            onError={() => setVideoFailed(true)}
          />
        </div>
        {soundButton}
      </>
    );
  }

  return <div className="hero-bg" style={{ backgroundImage: `url(${image})` }} />;
}

// ── Service / sub-service filtering (portfolio & videos) ──
const NO_FILTER = { service: null, sub: null };
const matchesFilter = (item, f) =>
  (!f.service || item.service_id === f.service) && (!f.sub || item.sub_service_id === f.sub);

// "Wedding & Engagement" (or the service name) shown on a photo/video
function itemLabel(services, item) {
  const svc = services.find(s => s.id === item.service_id);
  const sub = svc?.sub_services?.find(ss => ss.id === item.sub_service_id);
  return sub?.name || svc?.name || item.category || '';
}

// Two rows of chips: services that have items, then that service's
// sub-services that have items. Unfiled items only appear under "All".
function ServiceFilters({ items, services, filter, onChange }) {
  const withItems = services
    .map(s => ({ ...s, count: items.filter(it => it.service_id === s.id).length }))
    .filter(s => s.count > 0);
  if (!withItems.length) return null;

  const current = withItems.find(s => s.id === filter.service);
  const subs = (current?.sub_services || [])
    .map(ss => ({ ...ss, count: items.filter(it => it.sub_service_id === ss.id).length }))
    .filter(ss => ss.count > 0);

  return (
    <div className="service-filters reveal">
      <div className="portfolio-filters">
        <button className={`filter-btn${!filter.service ? ' active' : ''}`} onClick={() => onChange(NO_FILTER)}>All</button>
        {withItems.map(s => (
          <button
            key={s.id}
            className={`filter-btn${filter.service === s.id ? ' active' : ''}`}
            onClick={() => onChange({ service: s.id, sub: null })}
          >
            {s.name}<span className="filter-count">{s.count}</span>
          </button>
        ))}
      </div>
      {current && subs.length > 0 && (
        <div className="portfolio-filters sub-filters" key={current.id}>
          <button className={`filter-btn${!filter.sub ? ' active' : ''}`} onClick={() => onChange({ service: current.id, sub: null })}>
            All {current.name}
          </button>
          {subs.map(ss => (
            <button
              key={ss.id}
              className={`filter-btn${filter.sub === ss.id ? ' active' : ''}`}
              onClick={() => onChange({ service: current.id, sub: ss.id })}
            >
              {ss.name}<span className="filter-count">{ss.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NAV_SECTIONS = ['about', 'services', 'portfolio', 'team', 'contact'];

const SOCIAL_LINKS = [
  { key: 'social_instagram', label: 'Instagram' },
  { key: 'social_youtube',   label: 'YouTube' },
  { key: 'social_tiktok',    label: 'TikTok' },
  { key: 'social_linkedin',  label: 'LinkedIn' },
  { key: 'social_x',         label: 'X' },
];
// "instagram.com/x" → "https://instagram.com/x"
const normalizeUrl = (u = '') => {
  const s = String(u).trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

// Photos and videos share one portfolio grid
const videoThumb = (v) => {
  const ytId = getYouTubeId(v.youtube_url);
  return v.thumbnail_url || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : null);
};
const mergeMedia = (photos, videos) => [
  ...photos.map(p => ({ ...p, kind: 'photo', key: `p${p.id}` })),
  ...videos.map(v => ({ ...v, kind: 'video', key: `v${v.id}`, image_url: videoThumb(v) })),
].sort((a, b) =>
  (a.display_order || 0) - (b.display_order || 0) ||
  new Date(a.created_at) - new Date(b.created_at)
);
const stagger = (i, step = 0.08, max = 4) => ({ transitionDelay: `${(i % max) * step}s` });

export default function Home() {
  const [scrolled, setScrolled]               = useState(false);
  const [portFilter, setPortFilter]           = useState(NO_FILTER);
  const [mediaType, setMediaType]             = useState('all'); // all | photo | video
  const [menuOpen, setMenuOpen]               = useState(false);
  const [sendState, setSendState]             = useState({ status: 'idle', message: '' }); // idle | sending | sent | error
  const [lightbox, setLightbox]               = useState(null);
  const [showAllPortfolio, setShowAllPortfolio] = useState(false);
  const [activeSection, setActiveSection]     = useState('');
  const [navLight, setNavLight]               = useState(false);

  const showIntro               = useIntro();
  const [introDone, setIntroDone] = useState(!showIntro);

  const PORTFOLIO_LIMIT = 6;

  const [portfolio,    setPortfolio]    = useState([]);
  const [services,     setServices]     = useState([]);
  const [settings,     setSettings]     = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [videos,       setVideos]       = useState([]);
  const [team,         setTeam]         = useState([]);
  const [brands,       setBrands]       = useState([]);

  const rootRef = useRef(null);

  useSmoothScroll();
  useRevealAll(rootRef);
  useStackedSections(rootRef, { onActive: setActiveSection, onLight: setNavLight });

  useEffect(() => {
    fetchAll().then(d => {
      setPortfolio(d.portfolio);
      setServices(d.services);
      setSettings(d.settings);
      setSettingsLoaded(true);
      setVideos(d.videos);
      setTeam(d.team);
      setBrands(d.brands);
    });
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const locked = !!(menuOpen || lightbox);
    document.body.style.overflow = locked ? 'hidden' : '';
    lockScroll(locked);
    return () => { document.body.style.overflow = ''; lockScroll(false); };
  }, [menuOpen, lightbox]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const media            = mergeMedia(portfolio, videos);
  const inService        = media.filter(m => matchesFilter(m, portFilter));
  const hasBothKinds     = inService.some(m => m.kind === 'photo') && inService.some(m => m.kind === 'video');
  const typeFilter       = hasBothKinds ? mediaType : 'all';
  const filtered         = typeFilter === 'all' ? inService : inService.filter(m => m.kind === typeFilter);
  const visiblePortfolio = showAllPortfolio ? filtered : filtered.slice(0, PORTFOLIO_LIMIT);
  const portKey = `${portFilter.service}-${portFilter.sub}-${typeFilter}`;
  const photoCount = filtered.filter(m => m.kind === 'photo').length;
  const videoCount = filtered.length - photoCount;
  const moreLabel  = photoCount && videoCount ? 'Items' : videoCount ? 'Videos' : 'Photos';

  const changePortFilter = (f) => { setPortFilter(f); setMediaType('all'); setShowAllPortfolio(false); };
  const changeMediaType  = (t) => { setMediaType(t); setShowAllPortfolio(false); };

  // From a service card: open that service (or sub-service) in the portfolio,
  // optionally showing only its photos or only its videos.
  const goToService = (serviceId, subId = null, kind = 'all') => {
    const f = { service: serviceId, sub: subId };
    if (!media.some(m => matchesFilter(m, f))) return;
    setPortFilter(f);
    setMediaType(kind);
    setShowAllPortfolio(false);
    // wait for the filtered grid to render and the sections to re-measure
    setTimeout(() => smoothScrollTo('portfolio'), 80);
  };

  // The lightbox holds an index into `filtered`, so it can step through
  // photos and videos alike.
  const lbList = filtered;
  const openItem    = useCallback((idx) => setLightbox({ index: idx }), []);
  const navLightbox = useCallback((dir) => {
    setLightbox(lb => lb && { index: (lb.index + dir + lbList.length) % lbList.length });
  }, [lbList.length]);

  const lbItem = lightbox ? lbList[lightbox.index] : null;
  const lb = !lbItem ? null
    : lbItem.kind === 'video'
      ? { type: 'video', ytId: getYouTubeId(lbItem.youtube_url), src: lbItem.thumbnail_url, title: lbItem.title, cat: itemLabel(services, lbItem) }
      : { type: 'image', src: lbItem.image_url, title: lbItem.title, cat: itemLabel(services, lbItem) };

  // Booking form → backend → Gmail. The form is only cleared on success, so
  // nothing typed is lost if sending fails.
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    setSendState({ status: 'sending', message: '' });
    try {
      await axios.post(`${API}/api/contact`, data);
      form.reset();
      setSendState({ status: 'sent', message: '' });
      setTimeout(() => setSendState(s => (s.status === 'sent' ? { status: 'idle', message: '' } : s)), 6000);
    } catch (err) {
      setSendState({
        status: 'error',
        message: err.response?.data?.message || 'Could not send your message. Please check your connection and try again.',
      });
    }
  }, []);

  const scrollTo = (id) => {
    setMenuOpen(false);
    // let the menu's scroll lock release first
    requestAnimationFrame(() => smoothScrollTo(id));
  };

  // Social links from admin settings; empty ones are hidden
  const socials = SOCIAL_LINKS
    .map(s => ({ ...s, url: normalizeUrl(settings[s.key]) }))
    .filter(s => s.url);

  const brandRows = brands.length >= 6
    ? [brands.filter((_, i) => i % 2 === 0), brands.filter((_, i) => i % 2 === 1)]
    : [brands];

  const renderBrand = (brand) => {
    const content = brand.logo_url
      ? <img className="brand-logo" src={brand.logo_url} alt={brand.name} />
      : <span>{brand.name}</span>;
    return brand.website_url
      ? <a className="brand-item" href={brand.website_url} target="_blank" rel="noopener noreferrer">{content}</a>
      : <div className="brand-item">{content}</div>;
  };

  return (
    <div className="home" ref={rootRef}>
      {showIntro && !introDone && <IntroLoader onDone={() => setIntroDone(true)} />}

      {/* ── Navbar ── */}
      <nav className={`navbar${scrolled ? ' scrolled' : ''}${navLight && !menuOpen ? ' nav-light' : ''}${introDone ? ' ready' : ''}`}>
        <a href="#hero" className="nav-logo nav-enter" style={{ '--n': 0 }} aria-label="Boom Creations — home"
           onClick={e => { e.preventDefault(); scrollTo('hero'); }}>
          <img className="nav-logo-mark" src="/brand/mark-white.png" alt="" />
          <img className="nav-logo-word" src="/brand/wordmark-white.png" alt="Boom Creations" />
        </a>
        <ul className="nav-links">
          {NAV_SECTIONS.map((s, i) => (
            <li key={s} className="nav-enter" style={{ '--n': i + 1 }}>
              <a href={`#${s}`} onClick={e => { e.preventDefault(); scrollTo(s); }}>
                <span className={`nav-link-text${activeSection === s ? ' is-active' : ''}`}>{s}</span>
              </a>
            </li>
          ))}
        </ul>
        <button className="nav-cta nav-enter" style={{ '--n': NAV_SECTIONS.length + 1 }} onClick={() => scrollTo('contact')}>Book Now</button>
        <button className={`nav-hamburger${menuOpen ? ' open' : ''}`} onClick={() => setMenuOpen(o => !o)} aria-label="Menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* Mobile menu */}
      <div className={`mobile-menu${menuOpen ? ' open' : ''}`}>
        {NAV_SECTIONS.map((s, i) => (
          <a key={s} href={`#${s}`} style={{ '--n': i }} onClick={e => { e.preventDefault(); scrollTo(s); }}>{s}</a>
        ))}
      </div>

      {/* ── Hero ── */}
      <section className={`hero stack${introDone ? ' ready' : ''}`} id="hero">
        {settingsLoaded && <HeroBackground settings={settings} forceMute={!!lightbox} />}
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-title">
            {settings.hero_title || 'WE CRAFT VISUAL STORIES THAT LAST'}
          </h1>
          <p className="hero-sub">
            {settings.hero_subtitle || "Award-winning photography and cinematic videography for brands, events, and life's most meaningful moments."}
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => scrollTo('portfolio')}>View Portfolio</button>
            <button className="btn-outline" onClick={() => scrollTo('contact')}>Book a Session</button>
          </div>
        </div>
        <div className="hero-scroll">
          <div className="scroll-line" />
          <span>Scroll</span>
        </div>
      </section>

      {/* ── Portfolio ── */}
      <section className="section portfolio-section stack" id="portfolio">
        <div className="section-header reveal">
          <span className="section-tag">Our Work</span>
          <h2 className="section-title">THE PORTFOLIO</h2>
        </div>
        <ServiceFilters items={media} services={services} filter={portFilter} onChange={changePortFilter} />
        {hasBothKinds && (
          <div className="media-toggle reveal" role="group" aria-label="Show">
            {[['all', 'All'], ['photo', 'Photos'], ['video', 'Videos']].map(([t, label]) => (
              <button key={t} type="button" className={typeFilter === t ? 'active' : ''} onClick={() => changeMediaType(t)}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="portfolio-grid">
          {visiblePortfolio.map((item, idx) => (
            <div
              key={`${portKey}-${item.key}`}
              className={`portfolio-item reveal${item.tall ? ' tall' : ''}${item.kind === 'video' ? ' is-video' : ''}`}
              style={stagger(idx, 0.1, 3)}
              onClick={() => openItem(idx)}
            >
              {item.image_url
                ? <img src={item.image_url} alt={item.title} loading="lazy" />
                : <div className="portfolio-placeholder" />}
              {item.kind === 'video' && (
                <div className="portfolio-play" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                </div>
              )}
              <div className="portfolio-overlay">
                <div className="portfolio-info">
                  <div className="portfolio-cat">
                    {item.kind === 'video' && <span className="portfolio-kind">Video · </span>}
                    {itemLabel(services, item)}
                  </div>
                  <div className="portfolio-title">{item.title}</div>
                </div>
                <div className="portfolio-expand">{item.kind === 'video' ? '▶' : '⊕'}</div>
              </div>
            </div>
          ))}
        </div>
        {filtered.length > PORTFOLIO_LIMIT && (
          <div className="view-more-wrap">
            <button className="view-more-btn" onClick={() => setShowAllPortfolio(s => !s)}>
              {showAllPortfolio
                ? 'Show Less'
                : `View All ${filtered.length} ${moreLabel}`}
            </button>
            {!showAllPortfolio && (
              <span className="view-more-count">{filtered.length - PORTFOLIO_LIMIT} more not shown</span>
            )}
          </div>
        )}
      </section>

       {/* ── About ── */}
      <section className="section about-section stack" id="about">
        <div className="about-inner">
          <div className="about-image-wrap reveal reveal-img">
            <img
              src={settings.about_image || DEFAULT_ABOUT_IMAGE}
              alt="Behind the scenes at Boom Creations"
            />
            <div className="about-badge">
              <span className="about-badge-num">8+</span>
              <span className="about-badge-text">Years of Visual Excellence</span>
            </div>
          </div>
          <div className="about-text reveal">
            <span className="section-tag">Who We Are</span>
            <h2 className="section-title">We are a Creative<br />Film Production Agency</h2>
            {settings.about_text_1 && <p>{settings.about_text_1}</p>}
            {settings.about_text_2 && <p>{settings.about_text_2}</p>}
            <div className="about-signature">{settings.founder_name || 'Said A.'}</div>
            <div className="about-sig-title">{settings.founder_title || 'Founder & Creative Director'}</div>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="section stack" id="services">
        <div className="section-header reveal">
          <span className="section-tag">What We Do</span>
          <h2 className="section-title">OUR SERVICES</h2>
        </div>
        <div className="services-grid">
          {services.map((s, i) => {
            const photos = portfolio.filter(p => p.service_id === s.id).length;
            const vids   = videos.filter(v => v.service_id === s.id).length;
            const linked = photos + vids > 0;
            const subs   = s.sub_services || (s.items || []).map(name => ({ id: name, name }));
            return (
              <div
                className={`service-card reveal${linked ? ' is-linked' : ''}`}
                style={stagger(i, 0.1)}
                key={s.id || s.name}
                role={linked ? 'link' : undefined}
                tabIndex={linked ? 0 : undefined}
                onClick={linked ? () => goToService(s.id) : undefined}
                onKeyDown={linked ? e => { if (e.key === 'Enter') goToService(s.id); } : undefined}
              >
                <div className="service-icon">{s.icon}</div>
                <div className="service-name">{s.name}</div>
                <p className="service-desc">{s.description}</p>
                <ul className="service-list">
                  {subs.map(ss => {
                    const has = portfolio.some(p => p.sub_service_id === ss.id) || videos.some(v => v.sub_service_id === ss.id);
                    return (
                      <li key={ss.id}>
                        {has ? (
                          <button
                            type="button"
                            className="service-sub-link"
                            onClick={e => { e.stopPropagation(); goToService(s.id, ss.id); }}
                          >
                            {ss.name}<span aria-hidden="true">→</span>
                          </button>
                        ) : ss.name}
                      </li>
                    );
                  })}
                </ul>
                {linked && (
                  <div className="service-links">
                    {photos > 0 && (
                      <button type="button" onClick={e => { e.stopPropagation(); goToService(s.id, null, 'photo'); }}>
                        Photos <span>{photos}</span>
                      </button>
                    )}
                    {vids > 0 && (
                      <button type="button" onClick={e => { e.stopPropagation(); goToService(s.id, null, 'video'); }}>
                        Videos <span>{vids}</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      
   {/* ── Brands ── */}
      {brands.length > 0 && (
        <section className="section brands-section stack light" data-theme="light">
          <div className="section-header reveal">
            <span className="section-tag">Trusted By</span>
            <h2 className="section-title">BRANDS WE WORK WITH</h2>
          </div>
          <div className="brands-marquees reveal">
            {brandRows.map((row, i) => (
              <Marquee
                key={i}
                items={row}
                direction={i % 2 === 0 ? 'left' : 'right'}
                speed={i % 2 === 0 ? 40 : 30}
                renderItem={renderBrand}
              />
            ))}
          </div>
        </section>
      )}
     

      {/* ── Team ── */}
      {team.length > 0 && (
        <section className="section team-section stack" id="team">
          <div className="section-header reveal">
            <span className="section-tag">The People</span>
            <h2 className="section-title">MEET THE TEAM</h2>
          </div>
          <div className="team-grid">
            {team.map((member, i) => (
              <div className="team-card reveal" style={stagger(i, 0.1)} key={member.id}>
                <div className="team-photo-wrap">
                  {member.photo_url
                    ? <img src={member.photo_url} alt={member.name} loading="lazy" />
                    : <div className="team-photo-placeholder">👤</div>
                  }
                  <div className="team-photo-overlay" />
                </div>
                <div className="team-info">
                  <div className="team-name">{member.name}</div>
                  <div className="team-role">{member.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
   

      {/* ── Contact ── */}
      <section className="section contact-section stack-end" id="contact">
        <div className="contact-inner">
          <div className="contact-info reveal">
            <span className="section-tag">Get in Touch</span>
            <h2 className="section-title">LET'S CREATE<br />TOGETHER</h2>
            <p>
              Ready to bring your vision to life? Tell us about your project and we'll get back to you with a tailored proposal.
            </p>
            <div className="contact-detail">
              <div className="contact-item">
                <div className="contact-item-label">Email</div>
                <div className="contact-item-val">{settings.contact_email || 'hello@boomcreations.com'}</div>
              </div>
              <div className="contact-item">
                <div className="contact-item-label">Phone</div>
                <div className="contact-item-val">{settings.contact_phone || '+233 (0) 55 000 0000'}</div>
              </div>
              <div className="contact-item">
                <div className="contact-item-label">Studio</div>
                <div className="contact-item-val">{settings.contact_location || 'Accra, Ghana'}</div>
              </div>
            </div>
            {socials.length > 0 && (
              <div className="contact-socials">
                {socials.map(s => (
                  <a href={s.url} className="social-btn" key={s.key} target="_blank" rel="noopener noreferrer">{s.label}</a>
                ))}
              </div>
            )}
          </div>
          <form className="contact-form reveal" style={{ transitionDelay: '0.15s' }} onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="fname">First Name</label>
                <input id="fname" name="fname" type="text" placeholder="John" required />
              </div>
              <div className="form-group">
                <label htmlFor="lname">Last Name</label>
                <input id="lname" name="lname" type="text" placeholder="Doe" required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input id="email" name="email" type="email" placeholder="you@example.com" required />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone (optional)</label>
                <input id="phone" name="phone" type="tel" placeholder="+250 7xx xxx xxx" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="message">Tell us about your project</label>
              <textarea id="message" name="message" rows={5} placeholder="Date, location, and any details you'd like to share..." required />
            </div>
            {/* Spam trap: hidden from people, bots fill it in */}
            <div className="form-trap" aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>
            <button className="form-submit" type="submit" disabled={sendState.status === 'sending'}>
              {sendState.status === 'sending' ? 'Sending…'
                : sendState.status === 'sent' ? '✓ Message Sent'
                : 'Send Message'}
            </button>
            <p className={`form-note${sendState.status === 'error' ? ' is-error' : sendState.status === 'sent' ? ' is-sent' : ''}`} role="status" aria-live="polite">
              {sendState.status === 'error' ? sendState.message
                : sendState.status === 'sent' ? 'Thank you! We received your booking and will respond soon.'
                : ''}
            </p>
          </form>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="footer stack-end">
        <div className="footer-top">
          <div>
            <img className="footer-logo" src="/brand/logo-white.png" alt="Boom Creations — Innovative ideas for you" />
            <div className="footer-tagline"> Production Services , Post-production , Aerial Services <br/> — Kigali, Rwanda</div>
          </div>
          <nav className="footer-nav">
            <div className="footer-nav-col">
              <div className="footer-nav-title">Navigate</div>
              <ul>
                {['Services', 'Portfolio', 'Team', 'About', 'Contact'].map(l => (
                  <li key={l}>
                    <a href={`#${l.toLowerCase()}`} onClick={e => { e.preventDefault(); scrollTo(l.toLowerCase()); }}>{l}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="footer-nav-col">
              <div className="footer-nav-title">Services</div>
              <ul>
                {['Photography', 'Videography', 'Post-Production', 'Event Coverage'].map(l => (
                  <li key={l}><a href="#!" onClick={e => e.preventDefault()}>{l}</a></li>
                ))}
              </ul>
            </div>
            {socials.length > 0 && (
              <div className="footer-nav-col">
                <div className="footer-nav-title">Follow</div>
                <ul>
                  {socials.map(s => (
                    <li key={s.key}><a href={s.url} target="_blank" rel="noopener noreferrer">{s.label}</a></li>
                  ))}
                </ul>
              </div>
            )}
          </nav>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">© {new Date().getFullYear()} Boom Creations Ltd. All rights reserved.</span>
          <ul className="footer-legal">
            <li><a href="#!" onClick={e => e.preventDefault()}>Privacy Policy</a></li>
            <li><a href="#!" onClick={e => e.preventDefault()}>Terms of Use</a></li>
            <li><Link to="/admin/login" style={{ color: 'var(--muted)', fontSize: '0.72rem', fontWeight: 300 }}>Admin</Link></li>
          </ul>
        </div>
      </footer>

      {/* ── Lightbox ── */}
      {lb && (
        <div className="lightbox" data-lenis-prevent onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>

          {lb.type === 'image' && (
            <div className="lightbox-content" onClick={e => e.stopPropagation()}>
              <img src={lb.src} alt={lb.title} />
              <div className="lightbox-caption">
                {lb.cat && <span className="lightbox-cat">{lb.cat}</span>}
                <span className="lightbox-title">{lb.title}</span>
              </div>
            </div>
          )}

          {lb.type === 'video' && (
            <div className="lightbox-content lightbox-video-content" onClick={e => e.stopPropagation()}>
              {lb.ytId ? (
                <iframe
                  key={lb.ytId}
                  src={`https://www.youtube.com/embed/${lb.ytId}?autoplay=1&rel=0`}
                  title={lb.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : lb.src ? (
                <img src={lb.src} alt={lb.title} />
              ) : (
                <div className="lightbox-no-video">No playable video available</div>
              )}
              <div className="lightbox-caption">
                {lb.cat && <span className="lightbox-cat">{lb.cat}</span>}
                <span className="lightbox-title">{lb.title}</span>
              </div>
            </div>
          )}

          {lbList.length > 1 && (
            <>
              <button className="lightbox-nav lightbox-prev" onClick={e => { e.stopPropagation(); navLightbox(-1); }}>‹</button>
              <button className="lightbox-nav lightbox-next" onClick={e => { e.stopPropagation(); navLightbox(1); }}>›</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
